import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { EmptyState, Skeleton } from "@/components/common/EmptyState";
import { SearchInput } from "@/components/common/SearchInput";
import { money } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Plus, FileSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function CostCenters() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const { data: ccs, isLoading } = useQuery({ queryKey: ["cost-centers"], queryFn: () => get("/finance/cost-centers") });
  const create = useMutation({
    mutationFn: (body) => post("/finance/cost-centers", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cost-centers"] }); toast.success("Cost center created"); },
  });

  const filtered = useMemo(() => {
    return (ccs || []).filter((c) => {
      if (search && !`${c.name} ${c.project || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [ccs, search]);

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Cost Centers" subtitle="Budget vs. actual per delivery project.">
        <FormDialog title="New Cost Center" testid="new-cc-modal"
          trigger={<Button data-testid="new-cc-btn"><Plus className="mr-1.5 h-4 w-4" />New Cost Center</Button>}
          fields={[
            { name: "name", label: "Name", required: true },
            { name: "project", label: "Project" },
            { name: "budget", label: "Budget", type: "number", required: true },
          ]}
          onSubmit={(v) => create.mutateAsync(v)} />
      </PageHeader>

      <div className="mb-4 flex items-center gap-3">
        <SearchInput testid="costcenter-search" placeholder="Search name or project" value={search} onChange={setSearch} />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={FileSearch}
          title={(ccs || []).length === 0 ? "No cost centers yet" : "No cost centers found"}
          subtitle={(ccs || []).length === 0 ? "Create your first cost center to get started." : "Adjust your search."} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Cost Center</th><th className="px-4 py-3 font-semibold">Project</th>
              <th className="px-4 py-3 text-right font-semibold">Budget</th><th className="px-4 py-3 text-right font-semibold">Actual</th>
              <th className="px-4 py-3 text-right font-semibold">Consumed</th></tr></thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.project}</td>
                  <td className="px-4 py-3 text-right">{money(c.budget)}</td>
                  <td className="px-4 py-3 text-right">{money(c.actual)}</td>
                  <td className={cn("px-4 py-3 text-right font-semibold", c.variance > 90 ? "text-red-500" : c.variance > 70 ? "text-amber-500" : "text-emerald-600")}>{c.variance}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
